import { CheckSquare, Info, Plus, Square, Tag } from "@tamagui/lucide-icons";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { TouchableOpacity } from "react-native";
import {
  Circle,
  type GetThemeValueForKey,
  View,
  XStack,
  YStack,
} from "tamagui";
import { TKey } from "../../../shared/translations/translationKeys";
import { useApi } from "../../core/apis";
import { SortableDataItemList } from "../../core/components/SortableList";
import { TagsCloud } from "../../core/tags/TagsCloud";
import { TagsModal } from "../../core/tags/TagsModal";
import {
  HelperType,
  type HelpItem,
  HelpItemRequiredType,
} from "../../helper/HelperManager";
import { useHelper } from "../../helper/useHelper";
import { ModuleBlock } from "../../ui/Block";
import { Box } from "../../ui/Box";
import { CenterContent } from "../../ui/CenterContent";
import type { InfoRowItemProps } from "../../ui/InfoRow";
import { Input } from "../../ui/InputNew";
import { log } from "../../ui/log";
import { ModuleDataType } from "../core/BaseModuleDataModel";
import type CheckboxModuleModel from "./CheckboxModuleModel";
import type { CheckboxModelData } from "./data/CheckboxModel";

export const CheckboxModuleUI = observer(
  ({ module }: { module: CheckboxModuleModel }) => {
    const { modulesDataApi } = useApi();

    const moduleUid = module.uid;

    React.useEffect(() => {
      const unsub = modulesDataApi.watchDataForModuleUid(moduleUid);
      return () => {
        log.network(
          `Stopped watchDataForModuleUid from moduleUid ${moduleUid}`,
        );
        unsub();
      };
    }, [modulesDataApi, moduleUid]);

    const dataItems = module.sortedItems;

    const items: HelpItem[] = React.useMemo(
      () => [
        {
          type: HelpItemRequiredType.Required,
          label: "Add a first item to complete below",
          isCompleted: () => {
            return dataItems.length > 0;
          },
        },
        {
          type: HelpItemRequiredType.Tip,
          label: "You can sort completed items at the bottom in settings",
          isEnabled: () => !module.data.settings.sortCheckedAtBottom,
          isCompleted: () => {
            return false;
          },
        },
        {
          type: HelpItemRequiredType.Tip,
          label: "You can assign specific users to complete an item",
          isCompleted: () => {
            return false;
          },
        },
        {
          type: HelpItemRequiredType.Tip,
          label: "You can set tags and easily filter items",
          isCompleted: () => {
            return false;
          },
        },
      ],
      [module.data.settings, dataItems],
    );

    const helper = useHelper(module.uid, HelperType.Checkbox, items);

    return (
      <YStack testID="checkbox-module" gap="$space.medium">
        {helper}

        {module.data.settings.useTags && module.planTags.length > 0 && (
          <View marginVertical="$medium" marginHorizontal="$small">
            <TagsCloud
              tags={module.planTags}
              onPress={(tag) => {
                module.toggleActiveTag(tag);
              }}
            />
          </View>
        )}

        <View>
          {dataItems.length > 0 && (
            <Box gap={0}>
              <SortableDataItemList items={dataItems} />
            </Box>
          )}

          {dataItems.length === 0 && (
            <CenterContent
              muted
              title="Empty list"
              subTitle={"Add a first TODO item below."}
              icon={Info}
            />
          )}

          <XStack
            gap="$small"
            alignItems="center"
            backgroundColor={"white"}
            borderTopWidth={1}
            borderTopColor={"$divider"}
          >
            <Input
              submitOnEnter
              type="padded"
              iconFirst
              blurOnSubmit={false}
              attention={dataItems.length === 0}
              placeholder="Add item..."
              clearOnSubmit
              icon={Plus}
              onSubmitEditing={(e) => {
                const label = e.nativeEvent.text;
                module.addNewData<CheckboxModelData>(
                  ModuleDataType.Checkbox,
                  {
                    label,
                    tags: module.currentTag ? [module.currentTag.uid] : [],
                  },
                  TKey.ActivityCheckboxAdded,
                  { label },
                );
              }}
            />

            <TouchableOpacity
              onPress={() => {
                runInAction(() => {
                  module.showTagModal = true;
                });
              }}
            >
              <Circle
                marginRight="$large"
                borderColor={"$divider"}
                borderWidth={1}
                style={{ backgroundColor: module.currentTag?.color ?? "white" }}
                size={34}
              >
                <Tag
                  size={14}
                  color={
                    (module.currentTag?.textColor ??
                      "black") as GetThemeValueForKey<"color">
                  }
                />
              </Circle>
            </TouchableOpacity>
          </XStack>
        </View>

        <TagsModal
          planUid={module.plan.uid}
          visible={module.showTagModal}
          onClose={() => {
            runInAction(() => {
              module.showTagModal = false;
            });
          }}
          selectedTags={module.currentTag ? [module.currentTag] : []}
          onSelect={(tag) => {
            runInAction(() => {
              if (module.currentTag === tag) {
                module.currentTag = undefined;
              } else {
                module.currentTag = tag;
              }
              module.showTagModal = false;
            });
          }}
        />
      </YStack>
    );
  },
);

export const CheckboxModuleBlockUI = observer(
  ({ module }: { module: CheckboxModuleModel }) => {
    const infoRowItems = useMemo<InfoRowItemProps[]>(
      () => [
        {
          text: `${module.data.snapshot?.checked ?? 0} done`,
          icon: CheckSquare,
        },
        {
          text: `${module.data.snapshot?.unchecked ?? 0} remaining`,
          icon: Square,
        },
      ],
      [module.data.snapshot],
    );

    return <ModuleBlock module={module} infoRowItems={infoRowItems} />;
  },
);
